import React from "react";
import convertToNoDecimalValue from "../../../utils/convertToNoDecimalValue";
import {
  Button,
  Iconography,
  Loader,
  LoaderSizes,
  ThickNessLevel,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppSelector } from "../../../redux";
import { ProductType } from "../../../redux/slices/product-detail/Types";
import { useCartCheckoutPermissions } from "../../../hooks/useCartCheckoutPermissions";
import { useServiceCatalogPermissions } from "../../../hooks/useServiceCatalogPermissions";
import { getSubscriptionButtonState } from "../../../utils/subscriptionUtils";
import "./ServiceCard.scss";

interface ServiceCardProps {
  product: ProductType;
  onAddToCartClick: (
    productVariantId: string,
    sellingPlanID: string | undefined
  ) => void;
  onViewClick: (product: ProductType) => void;
  width?: number;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  product,
  onAddToCartClick,
  onViewClick,
  width,
}) => {
  const { addedProductID, addToCartLoading, supportServiceInCart, cartItems } =
    useAppSelector((state) => state.cart);
  const { contractStatusList } = useAppSelector((state) => state.plp);
  const { showCartButton, cartButtonDisabled, canEdit } =
    useCartCheckoutPermissions();
  const { actionsLocked: catalogActionsLocked } = useServiceCatalogPermissions();
  const addToCartLoadingStatus =
    addToCartLoading && addedProductID === product.variants.edges[0].node.id;
  const sellingPlanID =
    product.sellingPlanGroups.edges[0]?.node.sellingPlans.edges[0]?.node.id;

  const addedToCart = (): boolean => {
    for (let i = 0; i < cartItems?.lines?.edges?.length; i++) {
      if (cartItems.lines.edges[i].node.merchandise.product.id === product.id)
        return true;
    }
    return false;
  };

  let addToCartDisabledSupportService = false;
  let isScheduledOrActivePackage = false;
  const buttonState = getSubscriptionButtonState(
    contractStatusList,
    product?.variants?.edges[0]?.node?.sku
  );
  if (
    product.productType?.replaceAll(" ", "_").toLowerCase() ===
    "support_service"
  ) {
    addToCartDisabledSupportService =
      supportServiceInCart || buttonState.disabled;
    isScheduledOrActivePackage =
      buttonState.label === "Active" || buttonState.label === "Scheduled";
  }

  const addToCartDisabled = addToCartDisabledSupportService || addedToCart();

  return (
    <div className="service-card" style={width ? { width: `${width}px` } : {}}>
      <div className="img-wrapper">
        <img src={product.featuredImage?.url} alt="no img" />
      </div>
      <div className="info">
        <div className="row">
          <div className="title">{product.title}</div>
          <div className="price">
            $
            {convertToNoDecimalValue(
              product.priceRangeV2.maxVariantPrice.amount
            )}
          </div>
        </div>
        <div className="row">
          <div className="cart-button">
            {isScheduledOrActivePackage ? (
              <Button
                label={buttonState.label}
                variant="override"
                icon={<Iconography icon="circleCheck" />}
                disabled={catalogActionsLocked}
                onClick={() => {}}
              />
            ) : showCartButton ? (
              <>
                <Button
                  label={buttonState.label}
                  variant="primary"
                  icon={<Iconography icon="cartShopping" />}
                  disabled={
                    catalogActionsLocked ||
                    cartButtonDisabled ||
                    addToCartLoadingStatus ||
                    addToCartDisabled ||
                    product?.variants?.edges?.[0]?.node?.sku ===
                      "PS-SUPPORT-T300"
                  }
                  onClick={() => {
                    if (!canEdit || catalogActionsLocked) return;
                    onAddToCartClick(
                      product.variants.edges[0].node.id,
                      sellingPlanID
                    );
                  }}
                />
                {addToCartLoadingStatus && (
                  <Loader
                    variant={LoaderSizes.small}
                    thicknessLevel={ThickNessLevel.thick}
                  />
                )}
              </>
            ) : null}
          </div>
          <Button
            label="VIEW"
            variant="secondary"
            onClick={() => onViewClick(product)}
          />
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;
